import type { PrivacyRequestStatus, PrivacyRequestType, PrivacyRequestView } from "@closer/shared";
import { PRIVACY_REQUEST_STATUS_LABELS } from "@closer/shared";
import { Badge, Button, Card, FormField, Input, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * KİŞİSEL VERİ TALEPLERİ (CLOS-222 `[201]` S03).
 *
 * ⚠ Bu ekran organizasyon/sözleşme export'u (CLOS-234) DEĞİL, audit export'u (CLOS-194)
 * DEĞİL — yalnız hesabın KENDİ verisi (`docs bkz. lib/privacy/dataInventory.ts`).
 *
 * ⚠ Hassas işlem: parola (+ 2FA açıksa kod) `AccountClosureSection` ile AYNI desen —
 * açık oturum tek başına yeterli değil.
 *
 * ⚠ Silme talebi hesap AÇIKKEN "Saklama gereği nedeniyle ertelendi" sonucuna düşer;
 * ekran bunu AÇIKÇA söylüyor — sessizce "beklemede" göstermek kullanıcıyı önce hesabını
 * kapatması gerektiği konusunda karanlıkta bırakırdı.
 */

const DURUM_TONU: Record<PrivacyRequestStatus, "neutral" | "success" | "warning" | "danger"> = {
  requested: "neutral",
  processing: "neutral",
  ready: "success",
  evaluating: "neutral",
  retained: "warning",
  anonymizing: "neutral",
  completed: "success",
  failed: "danger",
  expired: "warning",
  cancelled: "neutral",
};

const TALEP_TURU_ADI: Record<PrivacyRequestType, string> = {
  export: "Verilerimi dışa aktarma",
  deletion: "Hesabımı ve verilerimi silme",
};

/**
 * Sunucunun `YENIDEN_DENENEBILIR` listesiyle BİRE BİR (`privacy/service.ts`): ekran kendi
 * kuralını icat etmiyor, sunucunun 409 diyeceği bir düğme çizmiyor.
 */
const YENIDEN_DENENEBILIR: ReadonlySet<PrivacyRequestStatus> = new Set([
  "failed",
  "expired",
  "cancelled",
]);

function StepUpForm({
  onConfirm,
  loading,
  onVazgec,
}: {
  onConfirm: (parola: string, kod: string) => void;
  loading: boolean;
  onVazgec: () => void;
}) {
  const [parola, setParola] = useState("");
  const [kod, setKod] = useState("");
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border p-3">
      <FormField label="Şifreniz" required>
        <Input
          aria-label="Şifreniz"
          type="password"
          autoComplete="current-password"
          value={parola}
          onChange={(e) => setParola(e.target.value)}
        />
      </FormField>
      <FormField label="Doğrulama kodu (2FA açıksa)">
        <Input
          aria-label="Doğrulama kodu (2FA açıksa)"
          value={kod}
          autoComplete="one-time-code"
          onChange={(e) => setKod(e.target.value)}
        />
      </FormField>
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onVazgec}>
          Vazgeç
        </Button>
        <Button
          type="button"
          loading={loading}
          disabled={parola === ""}
          onClick={() => onConfirm(parola, kod)}
        >
          Talebi onayla
        </Button>
      </div>
    </div>
  );
}

export function PrivacyRequestsSection() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [acikForm, setAcikForm] = useState<PrivacyRequestType | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  const sorgu = useQuery({
    queryKey: ["gizlilik", "talepler"],
    queryFn: async () => (await api<PrivacyRequestView[]>("/api/privacy/requests")).data,
  });

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();

  const talepOlustur = useMutation({
    mutationFn: async (input: {
      type: PrivacyRequestType;
      currentPassword: string;
      code?: string;
    }) =>
      (
        await api<PrivacyRequestView>("/api/privacy/requests", {
          method: "POST",
          body: JSON.stringify(input),
        })
      ).data,
    onSuccess: () => {
      setAcikForm(null);
      setHata(null);
      toast.success("Talebiniz alındı");
      void queryClient.invalidateQueries({ queryKey: ["gizlilik", "talepler"] });
    },
    onError: (error) => {
      setHata(error instanceof ApiRequestError ? error.message : "Talep oluşturulamadı");
    },
  });

  const iptalEt = useMutation({
    mutationFn: async (id: string) =>
      (await api<PrivacyRequestView>(`/api/privacy/requests/${id}/cancel`, { method: "POST" }))
        .data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["gizlilik", "talepler"] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "İptal edilemedi");
    },
  });

  /**
   * YENİDEN DENEME (CLOS-1117 · yetim uç E). `POST …/retry` sunucuda vardı, hiçbir ekran
   * çağırmıyordu: düşmüş bir dışa aktarmayı kullanıcı yeniden başlatamıyordu.
   *
   * ⚠ Yalnız `export` ve yalnız failed/expired/cancelled (`YENIDEN_DENENEBILIR`). Hazır
   * talebi yeniden başlatmak elindeki dosyayı yok ederdi; silme talebinin "yeniden
   * denemesi" diye bir yol yok — sunucu ikisine de 409 diyor.
   *
   * ⚠ `idempotency-key` gönderiliyor ama bu uçta ETKİSİZ: `idempotency()` tenant kapsamlı
   * ve hesap düzeyi uçta şeffaf geçiyor. Çift tıklamayı asıl durduran sunucunun durum
   * kilidi — ilk istek talebi `requested` yapar, ikincisi 409 alır.
   */
  const yenidenDene = useMutation({
    mutationFn: async (id: string) =>
      (
        await api<PrivacyRequestView>(`/api/privacy/requests/${id}/retry`, {
          method: "POST",
          headers: { "idempotency-key": idempotency.uret() },
        })
      ).data,
    onSuccess: () => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      toast.success("Dışa aktarma yeniden başlatıldı");
      void queryClient.invalidateQueries({ queryKey: ["gizlilik", "talepler"] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Yeniden denenemedi");
    },
  });

  const indir = useMutation({
    mutationFn: async (id: string) =>
      (await api<{ downloadUrl: string }>(`/api/privacy/requests/${id}/download`)).data,
    onSuccess: (veri) => {
      window.open(veri.downloadUrl, "_blank", "noopener,noreferrer");
    },
    onError: (error) => {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : "Dosya indirilemedi; bağlantının süresi dolmuş olabilir",
      );
    },
  });

  if (sorgu.isPending) {
    return (
      <Card title="Kişisel Veri Talepleri">
        <div role="status" aria-label="Talepler yükleniyor">
          <Skeleton lines={3} />
        </div>
      </Card>
    );
  }

  const talepler = sorgu.data ?? [];

  return (
    <Card title="Kişisel Veri Talepleri">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-fg-muted">
          Hesabınıza ait kişisel verileri (profil, güvenlik geçmişi, üyelikler) dışa aktarabilir
          veya kişisel verilerinizin silinmesini/anonimleştirilmesini talep edebilirsiniz.
          Organizasyonunuzun sözleşme ve denetim kayıtları bu talebe dahil değildir.
        </p>

        {hata ? (
          <p role="alert" className="text-sm text-danger">
            {hata}
          </p>
        ) : null}

        {talepler.length === 0 ? (
          <p className="text-sm text-fg-soft">Henüz bir talebiniz yok.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {talepler.map((t) => (
              <li key={t.id} className="flex flex-col gap-1 rounded-md border border-border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-fg-strong">
                    {TALEP_TURU_ADI[t.type]}
                  </span>
                  <Badge tone={DURUM_TONU[t.status]}>
                    {PRIVACY_REQUEST_STATUS_LABELS[t.status]}
                  </Badge>
                </div>
                {t.decisions ? (
                  <ul className="flex flex-col gap-0.5 text-xs text-fg-muted">
                    {t.decisions.map((k) => (
                      <li key={k.dataClass}>
                        {k.dataClass}: {k.outcome}
                        {k.reason ? ` — ${k.reason}` : ""}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {t.failureReason ? (
                  <p role="alert" className="text-xs text-danger">
                    {t.failureReason}
                  </p>
                ) : null}
                <div className="flex flex-wrap justify-end gap-2">
                  {t.status === "ready" ? (
                    <Button
                      type="button"
                      size="sm"
                      loading={indir.isPending}
                      onClick={() => indir.mutate(t.id)}
                    >
                      İndir
                    </Button>
                  ) : null}
                  {t.status === "requested" ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={iptalEt.isPending}
                      onClick={() => iptalEt.mutate(t.id)}
                    >
                      İptal et
                    </Button>
                  ) : null}
                  {t.type === "export" && YENIDEN_DENENEBILIR.has(t.status) ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={yenidenDene.isPending}
                      onClick={() => yenidenDene.mutate(t.id)}
                    >
                      Yeniden dene
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}

        {acikForm ? (
          <StepUpForm
            loading={talepOlustur.isPending}
            onVazgec={() => setAcikForm(null)}
            onConfirm={(parola, kod) =>
              talepOlustur.mutate({
                type: acikForm,
                currentPassword: parola,
                code: kod.trim() === "" ? undefined : kod.trim(),
              })
            }
          />
        ) : (
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setAcikForm("export")}>
              Verilerimi dışa aktar
            </Button>
            <Button type="button" variant="secondary" onClick={() => setAcikForm("deletion")}>
              Verilerimin silinmesini talep et
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
